/** Lesson list with progress and hover actions. */
'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  ChevronRightIcon,
  CompleteIcon,
  PlayVideoIcon,
  ReplayIcon,
  ResumeVideoIcon,
} from '../Icons/Icons';
import { LessonWithProgress } from '@/hooks/useUserLearningPaths';
import { getLessonTypeLabel, getLessonDurationDisplay, getLessonActionLabel } from '@/lib/utils/lesson-meta';

type LessonStatus = 'not_started' | 'in_progress' | 'completed';

/** Individual lesson item with hover actions */
const LessonItems = ({
  item,
  selectedCourse,
  url,
}: {
  item: Partial<LessonWithProgress> & { title: string; url: string; duration?: string };
  selectedCourse: string;
  url: string;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getStatus = (): LessonStatus => {
    if (item.userProgress?.isCompleted) return 'completed';
    if (item.userProgress?.hasStarted) return 'in_progress';
    return 'not_started';
  };

  const status = getStatus();
  const progressPercentage = item.userProgress?.percentage ?? 0;
  const isCompleted = item.userProgress?.isCompleted ?? false;

  // Action icon based on lesson status (shown on hover)
  const actionLabel = getLessonActionLabel(item, status);
  const duration = getLessonDurationDisplay(item);

  const renderActionIcon = () => {
    if (!isHovered) return null;

    switch (status) {
      case 'completed':
        return (
          <>
            <span className="cs-caption hidden text-nowrap md:block">
              {item.duration || actionLabel}
            </span>
            <ReplayIcon className="m-[2px] text-[var(--Icons-Medium)]" />
          </>
        );
      case 'in_progress':
        return (
          <>
            <span className="cs-caption hidden text-nowrap md:block">
              {item.duration || actionLabel}
            </span>
            <ResumeVideoIcon className="text-[var(--Icons-Medium)]" />
          </>
        );
      case 'not_started':
        return (
          <>
            <span className="cs-caption hidden text-nowrap md:block">
              {item.duration || actionLabel}
            </span>
            <PlayVideoIcon className="text-[var(--Icons-Medium)]" />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Link href={`${url}${item.url}`} key={item.title} prefetch={true}>
      <li
        key={item.title}
        className={`w-inherit flex flex-col gap-2 py-3 pe-0 hover:bg-[var(--Surfaces-Extended-Amethyst-Transparent-Soft)] lg:px-4 ${item.url === selectedCourse ? 'bg-[var(--Surfaces-Extended-Amethyst-Transparent-Soft)]' : 'bg-surface-1'}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`align-center flex min-h-[34px] flex-row justify-between`}>
          {/* 1 */}
          <div className="flex w-full flex-col items-start justify-center lg:w-[30%]">
            <h6 className="cs-h6 w-full text-[var(--Text-Default-Light)]" title={item.title}>
              {item.title.trim()}
            </h6>
            {/* Always show content type for lessons (url !== '/' excludes Course Overview) */}
            {item.url !== '/' && (
              <div className="flex flex-row items-center gap-2">
                <div className="cs-caption capitalize">
                  {getLessonTypeLabel(item)}
                </div>
                {duration ? (
                  <>
                    <div>
                      <span className="divider" />
                    </div>
                    <div className="cs-caption">{duration}</div>
                  </>
                ) : null}
              </div>
            )}
          </div>

          {/* 2 */}
          {item?.userProgress && (
            <div className="flex w-[30%] flex-row items-center gap-2">
              {isCompleted || progressPercentage > 0 ? (
                <>
                  <div className="flex h-1 w-[80%] flex-row bg-[var(--Surfaces-Overlay-Sheen-75)]">
                    <div
                      className={`h-1 ${
                        isCompleted
                          ? 'bg-[var(--Color-Extended-Green-Green-Shade)]'
                          : 'bg-[var(--Surfaces-Extended-Amethyst-Transparent-Light)]'
                      } transition-all duration-300`}
                      style={{ width: `${isCompleted ? 100 : progressPercentage}%` }}
                    ></div>
                  </div>
                  <div className="cs-caption hidden text-nowrap opacity-45 sm:block">
                    {isCompleted ? '100%' : `${Math.round(progressPercentage)}%`} Complete
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* 3 */}
          {item?.userProgress && (
            <div className="flex flex-row items-center justify-end gap-1 sm:w-[20%] md:w-[30%]">
              {status === 'completed' && !isHovered && (
                <>
                  <CompleteIcon className="text-[var(--Icons-Medium)]" />
                </>
              )}
              {renderActionIcon()}
              <div className="px-2">
                <ChevronRightIcon
                  size={20}
                  style={{ color: isHovered ? 'var(--Icons-Medium)' : 'var(--Icons-Soft)' }}
                />
              </div>
            </div>
          )}
        </div>
      </li>
    </Link>
  );
};

export default function LessonsMeta({
  selectedCourse,
  url,
  courses,
}: {
  selectedCourse: string;
  url: string;
  courses: LessonWithProgress[];
}) {
  return (
    <div
      className={`w-inherit bg-surface-1 h-[fill-content] overflow-y-scroll py-4 [scrollbar-width:none] lg:px-4 [&::-webkit-scrollbar]:hidden`}
    >
      <ul>
        <LessonItems
          item={{
            title: 'Course Overview',
            url: '/',
          }}
          selectedCourse={selectedCourse}
          url={url}
        />

        {courses.map((item) => (
          <LessonItems
            item={item}
            selectedCourse={selectedCourse}
            url={url}
            key={item.uid || item.title}
          />
        ))}
      </ul>
    </div>
  );
}
